import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
  Button,
  FormField,
  Grid,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  Textarea,
} from '@eluve/components';

import {
  FHIRDosage,
  FHIRMedication,
  FHIRMedicationOrder,
  FHIROrderFormValues,
  FREQUENCY_OPTIONS,
  ROUTE_OPTIONS,
} from './FHIRMedicineRecommendations.types';

const NumberInputWithButtons: React.FC<{
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}> = ({ value, onChange, min = 0, max = 999 }) => {
  const handleIncrement = () => {
    const newValue = Math.min(value + 1, max);
    onChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max(value - 1, min);
    onChange(newValue);
  };

  return (
    <Stack direction="horizontal">
      <button
        type="button"
        onClick={handleDecrement}
        disabled={value <= min}
        className="flex size-10 shrink-0 items-center justify-center rounded-l-md border border-r-0 border-border-primary bg-bg-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Icon name="Minus" />
      </button>
      <Input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const parsed = parseInt(e.target.value, 10);
          if (!isNaN(parsed)) {
            onChange(Math.max(min, Math.min(parsed, max)));
          }
        }}
        className="[appearance:textfield] rounded-none border-x-0 text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button
        type="button"
        onClick={handleIncrement}
        disabled={value >= max}
        className="flex size-10 shrink-0 items-center justify-center rounded-r-md border border-l-0 border-border-primary bg-bg-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Icon name="Plus" />
      </button>
    </Stack>
  );
};

const orderFormSchema = z.object({
  dose: z.string().min(1, 'Dose is required'),
  frequency: z.string().min(1, 'Frequency is required'),
  route: z.string().min(1, 'Route is required'),
  duration: z.string(),
  quantity: z.coerce.number().min(1, 'Quantity must be at least 1'),
  refills: z.coerce.number().min(0, 'Refills cannot be negative'),
  instructions: z.string(),
});

interface FHIRMedicineOrderFormProps {
  medication: FHIRMedication;
  suggestedDosage?: FHIRDosage;
  existingOrder?: FHIRMedicationOrder; // For editing existing orders
  onSubmit: (order: FHIRMedicationOrder) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const FHIRMedicineOrderForm: React.FC<FHIRMedicineOrderFormProps> = ({
  medication,
  suggestedDosage,
  existingOrder,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const isEditing = Boolean(existingOrder);

  const { control, handleSubmit, formState, setValue } =
    useForm<FHIROrderFormValues>({
      resolver: zodResolver(orderFormSchema),
      defaultValues: {
        dose: existingOrder?.dose ?? suggestedDosage?.dose ?? '',
        frequency:
          existingOrder?.frequency ??
          suggestedDosage?.frequency ??
          'once daily',
        route: existingOrder?.route ?? suggestedDosage?.route ?? 'oral',
        duration:
          existingOrder?.duration ?? suggestedDosage?.duration ?? '30 days',
        quantity: existingOrder?.quantity ?? 30,
        refills: existingOrder?.refills ?? 0,
        instructions: existingOrder?.instructions ?? '',
      },
    });

  const quantity = useWatch({ control, name: 'quantity' });
  const refills = useWatch({ control, name: 'refills' });

  const handleFormSubmit = handleSubmit((data) => {
    const order: FHIRMedicationOrder = {
      id: existingOrder?.id ?? `order-${Date.now()}`,
      medication,
      dose: data.dose,
      frequency: data.frequency,
      route: data.route,
      duration: data.duration || undefined,
      quantity: data.quantity,
      refills: data.refills,
      instructions: data.instructions || undefined,
    };
    onSubmit(order);
  });

  return (
    <div className="rounded-lg border border-border-primary bg-bg-secondary p-4">
      <form onSubmit={handleFormSubmit}>
        <Stack gap={4}>
          <Text size="xs" weight="semibold" color="secondary">
            Order Details
          </Text>

          <Grid columns={2} gap={4} className="md:grid-cols-4">
            <Label>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Dose *
                </Text>
                <FormField
                  control={control}
                  name="dose"
                  render={({ field }) => (
                    <Input placeholder="e.g., 10 mg" {...field} />
                  )}
                />
                {formState.errors.dose && (
                  <Text size="xs" className="text-text-negative">
                    {formState.errors.dose.message}
                  </Text>
                )}
              </Stack>
            </Label>

            <Label>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Frequency *
                </Text>
                <FormField
                  control={control}
                  name="frequency"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Stack>
            </Label>

            <Label>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Route *
                </Text>
                <FormField
                  control={control}
                  name="route"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select route" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROUTE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </Stack>
            </Label>

            <Label>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Duration
                </Text>
                <FormField
                  control={control}
                  name="duration"
                  render={({ field }) => (
                    <Input placeholder="e.g., 30 days" {...field} />
                  )}
                />
              </Stack>
            </Label>
          </Grid>

          <Grid columns={2} gap={4}>
            <Label>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Quantity to Dispense *
                </Text>
                <NumberInputWithButtons
                  value={quantity}
                  onChange={(value) => setValue('quantity', value)}
                  min={1}
                  max={999}
                />
                {formState.errors.quantity && (
                  <Text size="xs" className="text-text-negative">
                    {formState.errors.quantity.message}
                  </Text>
                )}
              </Stack>
            </Label>

            <Label>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Refills Allowed
                </Text>
                <NumberInputWithButtons
                  value={refills}
                  onChange={(value) => setValue('refills', value)}
                  min={0}
                  max={12}
                />
              </Stack>
            </Label>
          </Grid>

          <Label className="w-full">
            <Stack gap={1}>
              <Text size="xs" color="tertiary">
                Additional Instructions
              </Text>
              <FormField
                control={control}
                name="instructions"
                render={({ field }) => (
                  <Textarea
                    placeholder="e.g., Take with food, avoid alcohol..."
                    rows={2}
                    {...field}
                  />
                )}
              />
            </Stack>
          </Label>

          <Stack gap={2} direction="horizontal" justify="end">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFormSubmit}
              disabled={isSubmitting || !formState.isValid}
            >
              {isEditing ? 'Update Order' : 'Add to Orders'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </div>
  );
};
